import {
  LoadingOutlined,
  UploadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Card,
  Col,
  Descriptions,
  Row,
  Spin,
  Upload,
} from "antd";
import ImgCrop from "antd-img-crop";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import ModalChangePassword from "../../components/modal/ModalChangePassword";
import { useGlobalLoading } from "../../hooks/useGlobalLoading";
import { useUploadAvatar } from "../../query/UserQuery";
import { AppDispatch, RootState } from "../../store/index";
import { setUser } from "../../store/slices/authSlice";
import { notify } from "../../utils/toast";

const Profile: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.userInfo);
  const dispatch = useDispatch<AppDispatch>();
  const isLoading = useGlobalLoading();
  const uploadAvatar = useUploadAvatar();

  const [openModalChangePassword, setOpenModalChangePassword] =
    React.useState<boolean>(false);

  const handleOpenChangePassword = () => {
    setOpenModalChangePassword(true);
  };

  const handleAvatarUpload = async (file: File) => {
    const isImage = file.type.startsWith("image/") || file.type === "";
    if (!isImage && !file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      notify.error("Chỉ chấp nhận file ảnh (JPG, PNG, GIF, etc.)");
      return Upload.LIST_IGNORE;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      notify.error("Kích thước ảnh phải nhỏ hơn 5MB");
      return Upload.LIST_IGNORE;
    }

    try {
      const avatarUrl = await uploadAvatar.mutateAsync(file);
      notify.success("Cập nhật ảnh đại diện thành công!");
      if (user) {
        dispatch(setUser({ ...user, avatar: avatarUrl }));
      }
    } catch (error: any) {
      notify.error(
        error?.response?.data?.message ||
          "Có lỗi xảy ra khi tải ảnh lên. Vui lòng thử lại.",
      );
    }

    return false;
  };

  return (
    <div className="relative min-h-full">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-opacity-100 z-50 bg-[var(--bg-primary)]">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
        </div>
      )}
      <div className="min-h-full rounded-lg shadow-sm p-6 transition-colors duration-300">
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={8}>
            <Card
              className="text-center border-gray-200 dark:border-gray-600 transition-colors duration-300"
              bodyStyle={{ padding: "32px 24px" }}
            >
              <Avatar
                size={120}
                icon={<UserOutlined />}
                src={user?.avatar ? `${user.avatar}` : undefined}
                className="mb-4"
                style={{ backgroundColor: "var(--primary-color)" }}
              />

              <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
                {user?.fullName || "Người dùng"}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                {user?.position?.positionTitle || "Chưa cập nhật vị trí"}
              </p>
              <ImgCrop
                rotationSlider
                aspectSlider
                showGrid
                quality={1}
                fillColor="transparent"
                modalTitle="Chỉnh sửa ảnh"
                modalOk="Xác nhận"
                modalCancel="Hủy"
                cropShape="round"
                aspect={1}
              >
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={handleAvatarUpload}
                  disabled={uploadAvatar.isPending}
                >
                  <Button
                    type="primary"
                    icon={<UploadOutlined />}
                    size="large"
                    className="w-full"
                    loading={uploadAvatar.isPending}
                    style={{
                      backgroundColor: "var(--primary-color)",
                      borderColor: "var(--primary-color)",
                    }}
                  >
                    {uploadAvatar.isPending
                      ? "Đang tải lên..."
                      : "Chỉnh sửa ảnh đại diện"}
                  </Button>
                </Upload>
              </ImgCrop>
            </Card>
          </Col>

          <Col xs={24} lg={16}>
            <Card
              title={
                <span className="text-gray-800 dark:text-white text-lg font-semibold">
                  Thông tin cá nhân
                </span>
              }
              className="border-gray-200 dark:border-gray-600 transition-colors duration-300"
              bodyStyle={{ padding: "24px" }}
            >
              <Descriptions
                column={{ xs: 1, sm: 1, md: 2 }}
                labelStyle={{
                  color: "var(--text-secondary)",
                  fontWeight: "500",
                }}
                contentStyle={{
                  color: "var(--text-primary)",
                  fontWeight: "400",
                }}
              >
                <Descriptions.Item label="Họ và tên">
                  {user?.fullName || "Chưa cập nhật"}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  {user?.email || "Chưa cập nhật"}
                </Descriptions.Item>
                <Descriptions.Item label="Vai trò">
                  {user?.role || "Chưa cập nhật"}
                </Descriptions.Item>
                <Descriptions.Item label="Vị trí công việc">
                  {user?.position?.positionTitle || "Chưa cập nhật"}
                </Descriptions.Item>
                <Descriptions.Item label="Phòng ban">
                  {user?.department?.description || "Chưa cập nhật"}
                </Descriptions.Item>
                <Descriptions.Item label="Cấp bậc">
                  {user?.position?.level || "Chưa cập nhật"}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card
              title={
                <span className="text-gray-800 dark:text-white text-lg font-semibold">
                  Cài đặt tài khoản
                </span>
              }
              className="mt-6 border-gray-200 dark:border-gray-600 transition-colors duration-300"
            >
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="text-gray-800 dark:text-white font-medium">
                      Đổi mật khẩu
                    </h4>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                      Cập nhật mật khẩu để bảo mật tài khoản
                    </p>
                  </div>
                  <Button onClick={handleOpenChangePassword} type="default">
                    Đổi mật khẩu
                  </Button>
                  <ModalChangePassword
                    open={openModalChangePassword}
                    onCancel={() => setOpenModalChangePassword(false)}
                  />
                </div>

                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="text-gray-800 dark:text-white font-medium">
                      Xác thực hai yếu tố
                    </h4>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                      Tăng cường bảo mật cho tài khoản của bạn
                    </p>
                  </div>
                  <Button disabled type="default">
                    Cài đặt
                  </Button>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Profile;
